import * as Y from 'yjs'
import { computeChangedRegion } from '../../lib/diff'
import type { HistoryEntry, HistoryKind } from '../../lib/types'

export const HISTORY_CAP = 50
const CONTEXT = 40

/** Tags our own history writes so observers can tell them apart from content edits. */
export const historyOrigin = { src: 'history-capture' }

/**
 * A history entry over a MARKDOWN part: its text window is always present. HistoryEntry declares
 * `start`/`before`/`after` optional because a GRID entry carries `cells`/`structure` instead, so
 * this narrowing is what lets the markdown callers keep reading them without a null check.
 */
export type MarkdownHistoryEntry = HistoryEntry & { start: number; before: string; after: string }

/**
 * Build a HistoryEntry from a before/after pair, capturing ONLY the changed window
 * (+ frozen display context). Returns null when nothing actually changed.
 */
export function makeHistoryEntry(opts: {
  actor: string
  kind: HistoryKind
  before: string
  after: string
  prompt?: string
}): MarkdownHistoryEntry | null {
  const region = computeChangedRegion(opts.before, opts.after)
  if (region.removed === '' && region.added === '') return null
  return {
    id: crypto.randomUUID(),
    actor: opts.actor,
    kind: opts.kind,
    ...(opts.prompt ? { prompt: opts.prompt } : {}),
    start: region.start,
    before: region.removed,
    after: region.added,
    contextBefore: opts.before.slice(Math.max(0, region.start - CONTEXT), region.start),
    contextAfter: opts.after.slice(
      region.start + region.added.length,
      region.start + region.added.length + CONTEXT,
    ),
    createdAt: new Date().toISOString(),
  }
}

/** Append an entry to the room's history Y.Array, trimming the oldest beyond the cap. */
export function appendHistory(ydoc: Y.Doc, entry: HistoryEntry): void {
  const history = ydoc.getArray<Record<string, unknown>>('history')
  ydoc.transact(() => {
    history.push([entry as unknown as Record<string, unknown>])
    const drop = history.length - HISTORY_CAP
    if (drop > 0) history.delete(0, drop)
  }, historyOrigin)
}
